import { get, post } from 'tools/request'
import { AnalysisSettings } from 'components/analysis/AnalysisSettings'
import { analysisType, structuredQuery, importedCollections, questionAsParameters, currentAnalysisSelections, readonlyMode, faqOrReadOnly, liveParsed } from 'model/SearchQuery'
import {
	A,
	Button,
	Div,
	H2,
	P,
	not,
	reactive,
	Span,
	UL,
	LI
} from 'alkali'
import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import { EvidenceStatement } from 'components/evidence/EvidenceStatement'
import { Demographics } from 'components/filter/Demographics'
import { ImportCollectionsSelector } from 'components/filter/ImportCollections'
import { startExplanations, removeContents } from 'components/entry/Explanation'
import app from 'app/app'
import * as settingsIcon from 'icons/cog.svg'
import './AnalysisSelectionHeader.scss'
import { FAQButtons, StudiesTabs } from 'components/studies/StudiesTabs'
import { FilterButton } from 'components/navbar/FilterButton'
import { screening } from 'model/Screening'
import { NamingDialog } from 'components/screening/NamingDialog'
import Icon from 'components/Icon'
import * as TagIcon from 'icons/tags.svg'
import * as ChooseIcon from 'icons/cursor-choose.svg'
import { Toast, hideToast, getToastPosition } from 'components/generic/Toast'
import { questionToText } from '../../../portal/analysis/Question'


let hasReport = true //analysisType.to(analysisType =>
//	analysisType == 'auto' || analysisType == 'bayesian' || analysisType == 'frequentist' || analysisType == 'direct')
const analysisTypeObject =
{
	'auto': 'Automatic Analysis',
	'direct': 'Direct Meta Analysis',
	'bayesian': 'Bayesian NMA',
	'frequentist': 'Frequentist NMA',
	'cohort': 'Cohort Analysis',
	'evidence-table': 'Evidence Table Report',
	'custom': 'Custom Analysis',
}
let queryTags,
queryShortName,
queryQuestion

interface ScreeningObject {
	shortName ?: string,
	description ?: string
}
const toastMessage = () => {
	let queryDetails = currentAnalysisSelections.to(analysis => analysis).valueOf()
	queryQuestion = questionToText(liveParsed.annotations.valueOf())
	let screeningName = screening?.shortName.valueOf()
	let screeningDescription =  screening?.description.valueOf()
	let collection = queryDetails?.studyFilter?.importCollection
	let dsQuery = queryDetails.dsQuery?.name
	const message = Div([
		UL([
			LI([
				Span(queryQuestion),
			]),
			queryShortName && LI([
				Span('.strong',['Name']), Span(queryShortName),
			]),
			collection && LI([
				Span('.strong',['Collection']), Span(collection),
			]),
			dsQuery && LI([
				Span('.strong',['DocSearch Query']), Span(dsQuery),
			])
		]),
		(screeningName || screeningDescription) && UL([
			P('.strong', [
			'Screening'
			]),
			screeningName && LI([
				Span('.strong',['Name']), Span(screeningName),
			]),
			screeningDescription && LI([
				Span('.strong',['Description']), Span(screeningDescription),
			])
		]),
		queryTags && UL([
			P('.strong', [
			'Tags'
			]),
			Div('.analysis-tags', queryTags.map(tag => {return Div('.analysis-tag', [tag])}))
		])
	])
	return message
}

const displayToast = (event) => {
	const existingToast: HTMLElement = document.getElementById('naming-toast')
	if (existingToast) {
		existingToast.remove()
	}
	const resultBody = document.querySelector('#main')
	const parentLocation = getToastPosition(event.target)
	const toastPosition = {
		y: `calc(${parentLocation.position.vhPercent} + 6%)`,
	}
	const namingMessage = toastMessage()
	const newToast = NamingToast(toastPosition, namingMessage)
	resultBody.prepend(newToast)
}

const NamingToast = (toastPosition, namingMessage) => new Toast(`.active.top-arrow.hover-toast#naming-toast`, {
	hover: true,
	style: {position: 'absolute', left: `calc(100% - 420px)`, top: toastPosition.y, right: 0},
	child: true,
	title: 'Query Details',
	message: namingMessage,
})

export const SelectAnalysisType = (activeTab?) => Div('.analysis-views', [
	SelectivityDropdown('.analysis-type-selector', {
		placeholder: 'Analysis',
		showSearchInputInDropdown: false,
		disabled: activeTab && activeTab.to((activeTab) => activeTab == 'report'),
		items: [
			{ text: 'Automatic Analysis', id: 'auto' },
			{ text: 'Direct Meta Analysis', id: 'direct' },
			{ text: 'Bayesian NMA', id: 'bayesian' },
			{ text: 'Frequentist NMA', id: 'frequentist' },
			{ text: 'Cohort Analysis', id: 'cohort' },
			{
				text: 'Evidence Table Report',
				id: 'evidence-table'
			},
			{ text: 'Custom Analysis', id: 'custom', disabled: true },
		],
		value: analysisType,
	}),
	Button(
		'.run-settings-container',
		{
			onclick(event) {
				if (faqOrReadOnly.valueOf())
					return
				event.stopPropagation()
				new AnalysisSettings().show()
			},
		},
		[Icon('.settings-icon', { icon: settingsIcon })]
	),
])
export class AnalysisSelectionHeader extends Div(isFAQDashboard ? '.faq-selection' : '.analysis-selection') {
	created(properties) {
		let activeTab = properties.activeTab
		let evidenceResults = properties.evidenceResults
		properties.classes = {
			'analysis-view': activeTab.to((activeTab) => activeTab == 'report'),
			'sources-view': activeTab.to((activeTab) => activeTab == 'sources'),
			'screening-view': activeTab.to((activeTab) => activeTab == 'screenings'),
			'analyzable-view': activeTab.to((activeTab) => activeTab == 'analyzable'),
		}
		properties.content = [
			Div('.evidence-summary', [
				Div('.analysis-collections-area', [
					isFAQDashboard ?
						Div('.analysis-views', [
							P('.analysis-type-text', [analysisType.to(analysisType => analysisTypeObject[analysisType])])
						]) :
						Div('.analysis-views', SelectAnalysisType(activeTab)),
					FilterButton,
				]),
				H2('.report-header', ['Meta Analysis Report']),
				Demographics({
					display: activeTab.to(
						(activeTab) => activeTab != 'report'),
				}),
				Div('.analysis-results', [
					EvidenceStatement.with({ activeTab }),
					Div('.analysis-tabs', {}, [
						A('.analysis-tab.meta-analysis', {
							content: 'Meta Analysis Report',
							classes: {
								'disabled-link': not(hasReport),
							},
							onclick: () => {
								if (hasReport.valueOf()) evidenceResults.metaAnalysisReport()
							},
							target: '_blank',
						}),
						// query naming
						isFAQDashboard ?
						Div('.reference-url-container', {
							content: currentAnalysisSelections.referenceUrl.to(url =>
								url ? A('.analysis-tab', {
									content: 'Original Publication',
									href: url,
									target: '_blank',
								}) : null)
						}) :
						Div('.query-naming', {
							classes: {
								// can edit if either screening or query owner
								'can-edit': (currentAnalysisSelections.to(analysis => analysis.owner == app.user.Id)) || (screening.ownsScreening().to(async ownsScreening => ownsScreening))
							},
							content: [
								screening.shortName.valueOf() ? Span([screening.shortName], {
									title: screening.shortName.to(shortName =>
										screening.description.to(description => 'Name: ' + shortName + '\n' +
											(description ? 'Description: ' + description + '\n' : '') +
											'Click to edit name and description.'
										)),
								}) : null,
								Div('.analysis-tags', {
									content: currentAnalysisSelections.to(async ({ name, tags }) => {
										queryTags = tags
										queryShortName = name
										let noClickView = await faqOrReadOnly
										return [
											Span('.query-name.truncate', [
												name ? 
													name 
													: noClickView ?
														'Query Details' 
														: (tags?.length > 0) ? 
															'Add a Query Name' 
															: 'Add a Query Name or Tag'
											]),
											Icon('.screening-icon', { icon: ChooseIcon, display: screening.ownsScreening().to(Boolean) }),
											screening.shortName.valueOf() && Span([screening.shortName], {
												title: screening.shortName.to(shortName => screening.description.to(description => 'Name: ' + shortName + '\n' +
													(description ? 'Description: ' + description + '\n' : '') +
													'Click to edit name and description.'
												)),
												display: screening.ownsScreening().to(Boolean)
											}),
											(tags?.length > 0) ? Icon('.tag-icon', { icon: TagIcon }) : null
										]
									})
								})
							],
							onclick: async (event) => {
								(await app.user)
								let owner = currentAnalysisSelections.get('owner')
								if ((await faqOrReadOnly) || (owner && owner != app.user.Id))
									return
								else {
									// If screening is false, then 
									const isScreening = await screening.ownsScreening().to(Boolean)
									const editQuery = currentAnalysisSelections.to(analysis => analysis.owner == app.user.Id)
									if (isScreening || editQuery) {
										new NamingDialog(`${isScreening ? 'screening' : 'query'}`).show(event)
									}
								}
							},
							onmouseenter: (event) => {
								displayToast(event)
							},
							onmouseleave() {
								hideToast('naming-toast')
							},
						}),
					]),
				]),
			]),
		]
	}
}

